import { Link } from "react-router-dom";

interface WhyItem {
  icon: string;
  title: string;
  text: string;
}

interface WhyChooseUsProps {
  heading?: string;
  subtitle?: string;
  items?: WhyItem[];
}

const defaultItems: WhyItem[] = [
  {
    icon: "ri-hand-heart-line",
    title: "Honest, No-Pressure Assessments",
    text: "We tell you exactly what needs fixing — nothing more. Never pushed toward unnecessary work.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Fast Emergency Response",
    text: "Storm damage or sudden leak? We respond same-day because every hour of delay costs more.",
  },
  {
    icon: "ri-medal-line",
    title: "Premium Materials Only",
    text: "We install roofing systems rated to handle Colorado hail, snow loads, and UV exposure long-term.",
  },
  {
    icon: "ri-tools-line",
    title: "Seasoned Professional Crews",
    text: "Every roofer on our team is experienced, background-checked, and held to our quality standards.",
  },
  {
    icon: "ri-home-smile-line",
    title: "Clean Sites, Respectful Teams",
    text: "We treat your home like our own. No mess left behind, no damage to landscaping or driveways.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Lifetime Workmanship Warranty",
    text: "We stand behind every nail and every shingle. Our warranty proves we're confident in our work.",
  },
];

export default function WhyChooseUs({
  heading = "Why Homeowners Trust Thomas Roofing & Repair",
  subtitle = "Built on honest service and real results — here's why Colorado Springs homeowners keep calling us back.",
  items = defaultItems,
}: WhyChooseUsProps) {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left: Content */}
          <div>
            <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
              Why Choose Us
            </span>
            <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
              {heading}
            </h2>
            <p className="text-[#6B7280] font-body text-base lg:text-lg mb-10 leading-relaxed">
              {subtitle}
            </p>

            <div className="grid grid-cols-1 gap-4 mb-10">
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-4 p-4 rounded-lg hover:bg-[#F8F9FA] transition-colors duration-200"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#10B981]/10 shrink-0">
                    <i className={`${item.icon} text-[#10B981] text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-headline font-700 text-[#1A1A1A] text-sm mb-0.5">{item.title}</h3>
                    <p className="text-[#6B7280] font-body text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4">
              <Link
                to="/#contact"
                className="bg-[#10B981] hover:bg-emerald-500 text-white font-body font-700 text-sm px-7 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                Get My Free Inspection
              </Link>
              <a
                href="tel:+17195550100"
                className="flex items-center gap-2 text-[#1A1A1A] font-body font-600 text-sm cursor-pointer whitespace-nowrap hover:text-[#10B981] transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-[#10B981] text-sm"></i>
                </div>
                (719) 555-0100
              </a>
            </div>
          </div>

          {/* Right: Image stack */}
          <div className="relative">
            <div className="relative rounded-lg overflow-hidden w-full h-[520px]">
              <img
                src="https://public.readdy.ai/ai/img_res/7d368f1e58a1160adc415f82066d3942.jpg"
                alt="Thomas Roofing professional crew"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/50 to-transparent" />

              {/* Floating stat badges */}
              <div className="absolute bottom-6 left-6 right-6 flex gap-3">
                <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 flex-1">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#10B981]/15 shrink-0">
                    <i className="ri-building-4-fill text-[#10B981] text-base"></i>
                  </div>
                  <div>
                    <span className="font-headline font-900 text-[#1A1A1A] text-lg leading-none block">500+</span>
                    <span className="text-[#6B7280] font-body text-xs">Roofs Done</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 flex-1">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-amber-50 shrink-0">
                    <i className="ri-star-fill text-amber-400 text-base"></i>
                  </div>
                  <div>
                    <span className="font-headline font-900 text-[#1A1A1A] text-lg leading-none block">5.0</span>
                    <span className="text-[#6B7280] font-body text-xs">Google Rating</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 flex-1">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#10B981]/15 shrink-0">
                    <i className="ri-shield-check-fill text-[#10B981] text-base"></i>
                  </div>
                  <div>
                    <span className="font-headline font-900 text-[#1A1A1A] text-lg leading-none block">100%</span>
                    <span className="text-[#6B7280] font-body text-xs">Insured</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
